/**
 * Deferred-loading helpers shared by the embed loader and the React `/lazy` entry.
 *
 * Strategies:
 * - `immediate`:   run right away.
 * - `load`:        run once the window `load` event has fired.
 * - `idle`:        run when the browser is idle after `load` (default).
 * - `interaction`: run on the first user interaction (pointer, key, touch, scroll).
 *                  Crawlers never interact, so nothing is loaded for them.
 */
export type LoadStrategy = 'immediate' | 'load' | 'idle' | 'interaction';

export const LOAD_STRATEGIES: readonly LoadStrategy[] = ['immediate', 'load', 'idle', 'interaction'];

export const DEFAULT_LOAD_STRATEGY: LoadStrategy = 'idle';

/** Upper bound for waiting on an idle period, so a busy page still gets the widget. */
const IDLE_TIMEOUT_MS = 2000;
/** For `idle`: start waiting for an idle period at most this long after scheduling, even if `load` hasn't fired. */
const IDLE_MAX_WAIT_AFTER_START_MS = 8000;
/** Fallback delay for browsers without requestIdleCallback (Safari). */
const IDLE_FALLBACK_DELAY_MS = 200;

const INTERACTION_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'scroll', 'wheel'] as const;

export function parseLoadStrategy(
  value: string | null | undefined,
  fallback: LoadStrategy = DEFAULT_LOAD_STRATEGY,
): LoadStrategy {
  const normalized = value?.trim().toLowerCase();
  return (LOAD_STRATEGIES as readonly string[]).includes(normalized ?? '')
    ? (normalized as LoadStrategy)
    : fallback;
}

function whenWindowLoaded(callback: () => void): () => void {
  if (document.readyState === 'complete') {
    callback();
    return () => {};
  }
  window.addEventListener('load', callback, { once: true });
  return () => window.removeEventListener('load', callback);
}

function whenIdle(callback: () => void): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(callback, { timeout: IDLE_TIMEOUT_MS });
    return () => window.cancelIdleCallback(handle);
  }
  const handle = window.setTimeout(callback, IDLE_FALLBACK_DELAY_MS);
  return () => window.clearTimeout(handle);
}

/**
 * Schedules `run` according to `strategy`. `run` is called at most once.
 * Returns a function that cancels the pending run. Browser-only: call from
 * an effect or a client-side script, never during SSR.
 */
export function scheduleLoad(strategy: LoadStrategy, run: () => void): () => void {
  let done = false;
  let cancelPending: () => void = () => {};

  const runOnce = () => {
    if (done) return;
    done = true;
    cancelPending();
    run();
  };

  switch (strategy) {
    case 'immediate':
      runOnce();
      return () => {};

    case 'load':
      cancelPending = whenWindowLoaded(runOnce);
      break;

    case 'interaction': {
      const options: AddEventListenerOptions = { passive: true, capture: true };
      INTERACTION_EVENTS.forEach((name) => window.addEventListener(name, runOnce, options));
      cancelPending = () => INTERACTION_EVENTS.forEach((name) => window.removeEventListener(name, runOnce, options));
      break;
    }

    case 'idle':
    default: {
      let cancelIdle: () => void = () => {};
      let idleStarted = false;
      const startIdle = () => {
        if (idleStarted) return;
        idleStarted = true;
        cancelIdle = whenIdle(runOnce);
      };
      const cancelLoad = whenWindowLoaded(startIdle);
      // `load` can be delayed for a long time by slow third-party resources; don't let that
      // keep the widget away indefinitely.
      const maxWait = window.setTimeout(startIdle, IDLE_MAX_WAIT_AFTER_START_MS);
      cancelPending = () => {
        cancelLoad();
        window.clearTimeout(maxWait);
        cancelIdle();
      };
      break;
    }
  }

  return () => {
    done = true;
    cancelPending();
  };
}
